import { useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { centavosDoTexto } from '@/comuns/dinheiro';
import { diaIso, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';

export interface TransferenciaModalProps {
    /** Contas ativas, com o saldo de hoje. */
    contas: { id: string; nome: string; saldoCentavos: number }[];
    origemInicial?: string;
    onFechar: () => void;
    onTransferido: () => void;
}

/**
 * Transferência entre contas.
 *
 * Mostra o saldo das duas antes e depois de confirmar: mover dinheiro é duas
 * pernas, e ver só uma esconde o que sobra na outra. Passar do saldo avisa, mas
 * não trava — o caixa da recepção costuma estar à frente do sistema.
 */
export function TransferenciaModal({ contas, origemInicial, onFechar, onTransferido }: TransferenciaModalProps) {
    const aviso = useAviso();
    const idOrigem = useId();
    const idDestino = useId();
    const idValor = useId();
    const idData = useId();
    const idObservacao = useId();

    const origemPadrao = origemInicial ?? contas[0]?.id ?? '';
    const [origemId, setOrigemId] = useState(origemPadrao);
    const [destinoId, setDestinoId] = useState(contas.find((c) => c.id !== origemPadrao)?.id ?? '');
    const [valor, setValor] = useState('');
    const [data, setData] = useState(diaIso());
    const [observacao, setObservacao] = useState('');
    const [salvando, setSalvando] = useState(false);

    const origem = contas.find((c) => c.id === origemId);
    const destino = contas.find((c) => c.id === destinoId);
    const valorCentavos = centavosDoTexto(valor);
    const mesma = Boolean(origemId) && origemId === destinoId;
    const acimaDoSaldo = Boolean(origem && valorCentavos && valorCentavos > origem.saldoCentavos);
    const pode = Boolean(origem && destino && !mesma && valorCentavos && valorCentavos > 0 && data);

    const transferir = async () => {
        if (!pode || !valorCentavos || !origem || !destino) return;
        setSalvando(true);
        try {
            await dados().transferirEntreContas({
                origemId: origem.id, destinoId: destino.id, valorCentavos, data,
                ...(observacao.trim() ? { observacao } : {}),
            });
            aviso({ title: 'Transferência registrada', description: `${moeda(valorCentavos)} · ${origem.nome} → ${destino.nome}` });
            onTransferido();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível transferir', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={540} onClose={onFechar}
            title="Transferência entre contas"
            description="Não é receita nem despesa: o dinheiro só muda de conta."
            dirty={Boolean(valor.trim()) || Boolean(observacao.trim())} onSave={() => { void transferir(); }} saveLabel="Transferir"
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void transferir(); }} disabled={!pode || salvando}>{salvando ? 'Transferindo…' : 'Transferir'}</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                    <FormField label="Conta de origem" htmlFor={idOrigem} required>
                        <Select id={idOrigem} value={origemId} onChange={setOrigemId} options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                    </FormField>
                    <FormField label="Conta de destino" htmlFor={idDestino} required error={mesma ? 'Escolha uma conta diferente' : undefined}>
                        <Select id={idDestino} value={destinoId} onChange={setDestinoId} invalid={mesma} options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                    </FormField>
                    <FormField label="Valor" htmlFor={idValor} required hint={acimaDoSaldo ? 'Acima do saldo de hoje da conta de origem' : undefined}>
                        <Input id={idValor} value={valor} onChange={setValor} inputMode="decimal" placeholder="0,00" iconLeft={<span style={{ fontSize: 13 }}>R$</span>} />
                    </FormField>
                    <FormField label="Data" htmlFor={idData} required>
                        <Input id={idData} type="date" value={data} onChange={setData} />
                    </FormField>
                    <FormField label="Observação" htmlFor={idObservacao} style={{ gridColumn: '1 / -1' }}>
                        <Input id={idObservacao} value={observacao} onChange={setObservacao} placeholder="Opcional" />
                    </FormField>
                </div>

                {origem && destino && !mesma && valorCentavos !== null && valorCentavos > 0 && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '12px 14px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
                        <span style={{ flex: 1, minWidth: 160 }}>
                            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>{origem.nome}</span>
                            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(origem.saldoCentavos)} → {moeda(origem.saldoCentavos - valorCentavos)}</strong>
                        </span>
                        <Icon name="arrow-right" size={16} style={{ color: 'var(--neutral-500)', alignSelf: 'center' }} />
                        <span style={{ flex: 1, minWidth: 160, textAlign: 'right' }}>
                            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>{destino.nome}</span>
                            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(destino.saldoCentavos)} → {moeda(destino.saldoCentavos + valorCentavos)}</strong>
                        </span>
                    </div>
                )}
            </div>
        </Modal>
    );
}
